import { create } from 'zustand'

export type HealthStatus = 'online' | 'offline' | 'unknown'

interface HealthState {
  status: HealthStatus
  lastSuccess: number | null
  consecutiveFailures: number
  lastFailure: number | null
  recordSuccess: () => void
  recordFailure: () => void
  setStatus: (status: HealthStatus) => void
  reset: () => void
}

// 2+ consecutive failures OR >15s with no success → offline
const FAILURE_THRESHOLD = 2
const SUSTAINED_OUTAGE_MS = 15_000

function deriveStatus(
  prev: HealthStatus,
  consecutiveFailures: number,
  lastSuccess: number | null,
  lastFailure: number | null,
): HealthStatus {
  if (consecutiveFailures >= FAILURE_THRESHOLD) return 'offline'
  // Sustained-outage: had a known-good baseline, then no success for >15s
  if (
    lastFailure !== null &&
    lastSuccess !== null &&
    lastFailure - lastSuccess > SUSTAINED_OUTAGE_MS
  ) {
    return 'offline'
  }
  return prev
}

export const useHealthStore = create<HealthState>((set) => ({
  status: 'unknown',
  lastSuccess: null,
  consecutiveFailures: 0,
  lastFailure: null,
  recordSuccess: () => {
    set(() => ({
      lastSuccess: Date.now(),
      consecutiveFailures: 0,
      lastFailure: null,
      status: 'online',
    }))
  },
  recordFailure: () => {
    set((state) => {
      const lastFailure = Date.now()
      const consecutiveFailures = state.consecutiveFailures + 1
      return {
        consecutiveFailures,
        lastFailure,
        status: deriveStatus(state.status, consecutiveFailures, state.lastSuccess, lastFailure),
      }
    })
  },
  setStatus: (status) => set({ status }),
  reset: () =>
    set({
      status: 'unknown',
      lastSuccess: null,
      consecutiveFailures: 0,
      lastFailure: null,
    }),
}))

export const HEALTH_CONSTANTS = {
  FAILURE_THRESHOLD,
  SUSTAINED_OUTAGE_MS,
}
